import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../../Services/translation';
import {
  queryPredicate,
  querySuggestions,
  queryCategories,
  setSearchQuery,
  hideFullForm,
  selectItem,
  toggleShowFullForm,
  handleKeyDown,
} from '../../Actions/Clerk.action';
import { debounce } from 'lodash-es';
import Icon from '../Icon';
import { lockFocusToModal, GetSearchUrl } from '../../Utils/utils';
import Input from '../Input';
import { dataLayerSearch } from '../../Services/Gtm.service';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => {
  dispatch(queryPredicate(text));
  dispatch(querySuggestions(text));
  dispatch(queryCategories(text));

  debouncedGA4Search(text); // fire only once per meaningful pause

}, 200);

const debouncedGA4Search = debounce((term) => {
  if (term.length >= 3) {
    dataLayerSearch(term);
  }
}, 500);

const ClerkQuickSearch = () => {
  const { query, showFullForm, result } = useSelector((state) => state.clerk);
  const searchUrl = GetSearchUrl(query);
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();

  const clickHandler = useCallback(
    (event) => {
      const headerActions = document.getElementById('header');
      if (headerActions && !headerActions.contains(event.target)) {
        dispatch(hideFullForm());
        dispatch(selectItem(-1));
      }
    },
    [dispatch]
  );
  useEffect(() => {
    // listen for click event to hide the search when clicking outside
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  useEffect(() => {
    // set query value if it is avaialble in the Url
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
    }
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.toggle('clerk-search-results-opened', showFullForm);
    if (showFullForm) {
      const tabbableContainer = document.querySelector('.header__main');
      return lockFocusToModal(tabbableContainer, () => {
        searchInput.current?.focus();
        dispatch(hideFullForm());
        dispatch(selectItem(-1));
      });
    }
  }, [showFullForm, dispatch]);

  const onClose = (e) => {
    dispatch(setSearchQuery(''));
    searchInput.current?.focus();
    dispatch(toggleShowFullForm());
  };

  return (
    <div className="clerk-quick-search" role="search" ref={searchContainer}>
      <a
        className="clerk-quick-search__link--block"
        rel="nofollow"
        tabIndex="-1"
        href="#"
        title={translate('general.search')}
        onClick={(e) => {
          e.preventDefault();
          dispatch(toggleShowFullForm());
          setTimeout(() => {
            searchInput.current && searchInput.current.focus();
          }, 0);
        }}
      >
        <i className="clerk-quick-search__icon"></i>
        <span className="clerk-quick-search__title hide">
          {translate('general.search')}
        </span>
      </a>
      <div
        className={`clerk-quick-search__form ${
          showFullForm ? 'clerk-quick-search__form--force-show' : ''
        }`}
        role="search"
      >
        <div className="clerk-quick-search__input-wrapper">
          {showFullForm ? (
            <button
              type="button"
              className="clerk-quick-search__close-button"
              aria-label={translate('clerk.quick-search.close.sc')}
              aria-hidden={!showFullForm}
              onClick={onClose}
            >
              <Icon
                size={24}
                name={'Close'}
                className="clerk-quick-search__icon-close"
              />
            </button>
          ) : (
            <div className="clerk-quick-search__search-icon">
              <Icon
                size={24}
                name={'Search'}
                className="clerk-quick-search__icon-search"
              />
            </div>
          )}
          <Input
            className="clerk-quick-search__input"
            type="search"
            placeholder={translate('general.search')}
            autoComplete="off"
            value={decodeURIComponent(query)}
            onChange={(event) => {
              const text = encodeURIComponent(event.target.value);
              dispatch(setSearchQuery(text));
              debouncedQuery(dispatch, text);
            }}
            onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
            ref={searchInput}
            onFocus={() => {
              if (query && !showFullForm) {
                dispatch(toggleShowFullForm());

                if (result === null) {
                  debouncedQuery(dispatch, query);
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClerkQuickSearch;
